import React, { useState, useEffect } from 'react';
import { Project, CreateProject, UpdateProject, Customer, CreateProjectPerson } from '../types';
import SearchableDropdown from './SearchableDropdown';
import DateInput from './DateInput';
import { PROJECT_CATEGORY_LABELS } from '../utils/constants';
import CreatableSelect from 'react-select/creatable';
import ProjectPersons from './ProjectPersons';
import { api, projectPersonsAPI, projectTagsAPI } from '../services/api';

interface ProjectFormProps {
  project?: Project;
  customers: Customer[];
  onSubmit: (project: CreateProject | UpdateProject, pendingPersons?: CreateProjectPerson[]) => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  availableTags: { id: number; value: string; label: string }[];
}

const ProjectForm: React.FC<ProjectFormProps> = ({
  project,
  customers,
  onSubmit,
  onCancel,
  isLoading = false,
  availableTags,
}) => {
  const [formData, setFormData] = useState<CreateProject | UpdateProject>({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    po: '',
    customerId: 0,
    statusId: 3,
    category: undefined,
    tags: '',
    statusReason: '',
    showInTimeTracking: false,
    isDayOffProject: false,
    tagIds: [],
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [pendingPersons, setPendingPersons] = useState<CreateProjectPerson[]>([]);
  const [tagOptions, setTagOptions] = useState<{ id: number; value: string; label: string }[]>(availableTags);
  const projectTagsByName = new Map(
    tagOptions.map(tag => [tag.value.toLowerCase(), tag.id])
  );

  const [statuses, setStatuses] = useState<{ id: number; title: string }[]>([]);
  const [loadingStatuses, setLoadingStatuses] = useState<boolean>(true);

  const loadStatuses = async () => {
    setLoadingStatuses(true);
    try {
      const response = await api.get('/statuses');
      const statusesData = response.data.data || response.data;

      setStatuses([
        { id: 0, title: 'Select status...' },
        ...statusesData,
      ]);
    } catch (error) {
      console.error('Error loading statuses:', error);
      setStatuses([{ id: 0, title: 'Error loading statuses' }]);
    } finally {
      setLoadingStatuses(false);
    }
  };

  useEffect(() => {
    loadStatuses();
  }, []);

  useEffect(() => {
    if (project) {
      const projectTagNames = (project.projectTags || [])
        .map(tag => tag?.name)
        .filter((name): name is string => Boolean(name && name.trim()))
        .map(name => name.trim()) || [];
      const tagsValue = projectTagNames.join(',');

      setFormData({
        id: project.id,
        title: project.title,
        description: project.description || '',
        date: project.date.split('T')[0],
        po: project.po || '',
        customerId: project.customerId,
        statusId: project.statusId,
        category: project.category,
        statusReason: project.statusReason,
        tags: tagsValue,
        showInTimeTracking: project.showInTimeTracking ?? false,
        isDayOffProject: project.isDayOffProject ?? false,
        tagIds: (project.projectTags || [])
          .map(tag => tag?.id)
          .filter((id): id is number => typeof id === 'number') || [],
      });
    }
  }, [project]);

  useEffect(() => {
    setTagOptions(availableTags);
  }, [availableTags]);

  const getSelectedTags = () => (
    formData.tags
      ? formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      : []
  );

  const handleCreateTag = async (inputValue: string) => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    try {
      const created = await projectTagsAPI.createProjectTag({ name: trimmed });
      const newOption = { id: created.id, value: created.name, label: created.name };

      setTagOptions(prev => {
        const exists = prev.some(option => option.value.toLowerCase() === newOption.value.toLowerCase());
        return exists ? prev : [...prev, newOption];
      });

      const currentTags = getSelectedTags();
      if (!currentTags.some(tag => tag.toLowerCase() === newOption.value.toLowerCase())) {
        setFormData(prev => ({
          ...prev,
          tags: [...currentTags, newOption.value].join(','),
          tagIds: prev.tagIds ? [...prev.tagIds, created.id] : [created.id],
        }));
      }
    } catch (error) {
      console.error('Error creating project tag:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : name === 'statusId' || name === 'customerId'
          ? (value === '' ? undefined : parseInt(value))
          : name === 'category'
          ? (value === '' ? undefined : parseInt(value))
          : value,
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.customerId || formData.customerId < 1) newErrors.customerId = 'Customer is required';
    if (!formData.statusId || formData.statusId < 1) newErrors.statusId = 'Status is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      await onSubmit(formData, project ? undefined : pendingPersons);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const categoryOptions = Object.entries(PROJECT_CATEGORY_LABELS).map(([value, label]) => ({
    value: parseInt(value),
    label,
  }));

  return (
    <div className="card project-form-card">
      <div className="card-header">
        <h5 className="mb-0">
          {project ? `Edit Project: ${project.title}` : 'Create New Project'}
        </h5>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit} className="project-form">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="title" className="form-label">
                Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              {errors.title && <div className="invalid-feedback">{errors.title}</div>}
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="statusId" className="form-label">
                Status <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${errors.statusId ? 'is-invalid' : ''}`}
                id="statusId"
                name="statusId"
                value={formData.statusId || ''}
                onChange={handleInputChange}
                disabled={isLoading || loadingStatuses}
              >
                {loadingStatuses ? (
                  <option>Loading...</option>
                ) : (
                  statuses.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.title}
                    </option>
                  ))
                )}
              </select>
              {errors.statusId && <div className="invalid-feedback">{errors.statusId}</div>}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="tags" className="form-label">
                Tags
              </label>
              <CreatableSelect
                id="tags"
                isMulti
                isClearable
                placeholder="Select or create tags..."
                value={
                  formData.tags
                    ? formData.tags.split(',').map(t => ({ value: t.trim(), label: t.trim() }))
                    : []
                }
                onChange={(selected) => {
                  const selectedValues = selected ? selected.map(s => s.value) : [];
                  const selectedIds = selected
                    ? selected
                      .map(s => projectTagsByName.get(s.value.toLowerCase()))
                      .filter((id): id is number => typeof id === 'number')
                    : [];

                  setFormData(prev => ({
                    ...prev,
                    tags: selectedValues.join(','),
                    tagIds: selectedIds,
                  }));
                }}
                onCreateOption={handleCreateTag}
                options={tagOptions}
                isDisabled={isLoading}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    background: state.isFocused ? 'var(--surface-hover)' : 'var(--surface)',
                    border: `1px solid ${state.isFocused ? 'var(--border-focus)' : 'var(--border-color)'}`,
                    boxShadow: state.isFocused ? '0 0 0 3px rgba(99, 102, 241, 0.1)' : 'none',
                    padding: '0.4rem',
                    borderRadius: 'var(--radius-md) !important',
                  }),
                  dropdownIndicator: () => ({ display: 'none' }),
                  indicatorSeparator: () => ({ display: 'none' }),
                }}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="statusReason" className="form-label">
                Status Reason
              </label>
              <input
                className="form-control"
                id="statusReason"
                name="statusReason"
                value={formData.statusReason}
                onChange={handleInputChange}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <SearchableDropdown
                id="customerId"
                label="Customer"
                placeholder="Select customer..."
                items={customers}
                value={formData.customerId}
                onChange={(value) => setFormData(prev => ({ ...prev, customerId: Number(value) }))}
                getItemValue={(customer) => customer.id}
                getItemLabel={(customer) => customer.title}
                disabled={isLoading}
                required
                error={errors.customerId}
                searchPlaceholder="Search customers..."
                noItemsMessage="No customers found"
              />
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="po" className="form-label">
                PO
              </label>
              <input
                type="text"
                className="form-control"
                id="po"
                name="po"
                value={formData.po || ''}
                onChange={handleInputChange}
                disabled={isLoading}
                maxLength={20}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="date" className="form-label">
                Date <span className="text-danger">*</span>
              </label>
              <DateInput
                className={`form-control ${errors.date ? 'is-invalid' : ''}`}
                id="date"
                name="date"
                value={formData.date}
                onChange={(value) => handleInputChange({ target: { name: 'date', value } } as any)}
                disabled={isLoading}
                required
              />
              {errors.date && <div className="invalid-feedback">{errors.date}</div>}
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="category" className="form-label">
                Category
              </label>
              <select
                className="form-select"
                id="category"
                name="category"
                value={formData.category !== undefined ? formData.category : ''}
                onChange={handleInputChange}
                disabled={isLoading}
              >
                <option value="">Select category...</option>
                {categoryOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3 d-flex align-items-center">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showInTimeTracking"
                  name="showInTimeTracking"
                  checked={formData.showInTimeTracking}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                <label className="form-check-label" htmlFor="showInTimeTracking">
                  Show in time tracking
                </label>
              </div>
            </div>

            <div className="col-md-6 mb-3 d-flex align-items-center">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="isDayOffProject"
                  name="isDayOffProject"
                  checked={formData.isDayOffProject}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                <label className="form-check-label" htmlFor="isDayOffProject">
                  Day off project
                </label>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="description" className="form-label">
              Description
            </label>
            <textarea
              className="form-control"
              id="description"
              name="description"
              rows={3}
              value={formData.description || ''}
              onChange={handleInputChange}
              disabled={isLoading}
            />
          </div>

          <div className="mb-3">
            <ProjectPersons
              projectId={project?.id}
              isEditing={true}
              onPendingPersonsChange={setPendingPersons}
            />
          </div>

          <div className="d-flex justify-content-start gap-2 form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : project ? 'Update' : 'Create'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectForm;
